/**
 * PlateDocumentDrawer — the reusable Plate-based document editor drawer.
 *
 * This is the generic engine behind every Plate "letter/consent/one-pager"
 * surface (Engagement Letter, NDA, Bureau & KYC Consent, …). It owns ALL the
 * shared behaviour — saved-doc fetch + Slate→Plate conversion, the BottomDrawer
 * chrome, PDF export (raster + print), regenerate, the comments side-pane, and
 * save (image processing + persist + Mixpanel) — and delegates only the
 * doc-specific bits to props:
 *
 *   • redux slice values  → orgId / companyName / loading / onClose / setLoading
 *   • API specifics       → buildInitialTemplate() (the doc's own template fetch)
 *   • identity / chrome    → docType / title / ids / upload sources / save event
 *   • optional transform   → normalizeOnLoad() (e.g. EL strips legacy 16px marks)
 *
 * To add a new Plate document type, write a ~30-line wrapper that reads its
 * redux slice and passes these props — no chrome/export/save code to copy.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { getOnePagerTemplateData, saveOnePagerTemplateData } from '../../store/investor/investorApiHelper';
import { regenerateIcon, PURPLE_DROPDOWN_CARET } from '../../assets/hostedassets';
import {
    PlateEditor,
    PreviewButton,
    slateToPlateValue,
    isSlateShape,
    processImagesBeforeSave,
    collectDocIdsFromSlate,
    exportEditorToPdfServer,
    exportEditorToDocxFile,
    ProgressBorder,
    ExportingLabel,
    useExportProgress,
} from '../PlateEditor';
import { toast } from 'react-toastify';
import { Button, Modal } from '../_custom';
import { BottomDrawer } from '../BottomDrawer';
import LoadingSpinner from '../_custom/LoadingSpinner/LoadingSpinner';
import { Mixpanel } from '../../utils/mixpanel';
import { getSubdomain } from '../../utils/utils';
import { useFeatureVisibility } from '../../utils/useVisibilityConfig';
import { VISIBILITY_KEYS } from '../../utils/constants/visibilityKeys';
import { resetAndHideCommentsThread, closeCommentsThread } from '../../store/commentsThread/commentsThread_actions';
import styles from './PlateDocumentDrawer.module.scss';

export type PlateDocType = 'ENGAGEMENT_LETTER' | 'NDA' | 'BUREAU_KYC_CONSENT' | 'ONE_PAGER' | 'APPLICATION_FORM';

export interface BuildInitialTemplateArgs {
    orgId: string;
    investorOrgId: string;
    emailId: string;
    companyName: string;
    companyMainImg: string;
    dispatch: ReturnType<typeof useAppDispatch>;
}

export interface PlateDocumentDrawerProps {
    /** Persistence key — selects which saved doc to load and where to save. */
    docType: PlateDocType;
    /** Header title + regenerate-copy noun, e.g. "Engagement Letter". */
    title: string;
    /** Export filename prefix, e.g. "Engagement_Letter". */
    fileNamePrefix: string;
    /** Unique BottomDrawer id (one per doc surface). */
    drawerId: string;
    /** Unique toolbar portal host id (one per doc surface). */
    toolbarHostId: string;
    /** Unique comments portal target id (one per doc surface). */
    commentContainerId: string;
    /** PlateEditor image upload source bucket, e.g. "engagement-letter". */
    uploadSource: string;
    /** processImagesBeforeSave document key, e.g. "engagementLetter". */
    imageDocumentType: string;
    /** Mixpanel event fired on save. */
    mixpanelSaveEvent: string;

    // ── doc-specific redux slice values (supplied by the wrapper) ──
    orgId: string;
    companyName: string;
    loading: boolean;
    onClose: () => void;
    setLoading: (loading: boolean) => void;

    /**
     * Per-doc fallback template builder. Called when no saved doc exists (or on
     * regenerate). Encapsulates the doc's own template-fetch + metadata flow and
     * returns the initialized (Slate- or Plate-shape) value. MUST be referentially
     * stable (wrap in useCallback) — it's in the fetch effect's deps.
     */
    buildInitialTemplate: (args: BuildInitialTemplateArgs) => Promise<any[]>;

    /** Optional load-time transform on the converted Plate value. */
    normalizeOnLoad?: (value: any[]) => any[];

    /**
     * View-to-attach (RECUR-23138): when the drawer was opened from Compose
     * Email, this captures the rendered editor to a PDF and hands the docId
     * back to the modal (as an attachment chip) after Save & Close, before the
     * drawer unmounts. Supplied by the wrapper from its redux slice's
     * `onComposeAttach`; absent in the normal (non-compose) open path.
     */
    onComposeAttach?: (canvasEl: HTMLElement, filename: string) => Promise<void>;
}

export function PlateDocumentDrawer({
    docType,
    title,
    fileNamePrefix,
    drawerId,
    toolbarHostId,
    commentContainerId,
    uploadSource,
    imageDocumentType,
    mixpanelSaveEvent,
    orgId,
    companyName,
    loading,
    onClose,
    setLoading,
    buildInitialTemplate,
    normalizeOnLoad,
    onComposeAttach,
}: PlateDocumentDrawerProps) {
    const dispatch = useAppDispatch();
    const ownerOptions = useAppSelector((state) => state.investor.ownerDropdownOptions) || [];
    const commentsThreadState = useAppSelector((state) => state.commentsThreadReducer);
    const commentsActive = Boolean(commentsThreadState?.showCommentsThread && !commentsThreadState?.closingCommentsThread);

    // Gate the AI assistant on the same `editor.AI.enabled`.write flag the
    // floating-toolbar "Ask Aica" button self-gates on. Without this, the button
    // rendered (flag on) but the AIMenu never mounted and the toggle listener
    // never attached (enableAI defaulted false) — clicking it did nothing.
    const { aiEnabled } = useFeatureVisibility({ aiEnabled: VISIBILITY_KEYS.AI.ENABLED });

    const investorOrgId = useAppSelector((state) => state.user.userData.investorOrgId);
    const emailId = useAppSelector((state) => state.user.userData.primaryEmail);
    const user = useAppSelector((state) => state.user.userData);
    const companyMainImg = useAppSelector((state) => state.user.domainMetadata?.mainImageUrl);

    const [editorInitialData, setEditorInitialData] = useState<any>(null);
    const [editorReady, setEditorReady] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showRegenConfirmModal, setShowRegenConfirmModal] = useState(false);
    const exportProgress = useExportProgress();
    // Derived so the existing `disabled={… || exporting}` guards keep working.
    const exporting = exportProgress.isRunning;
    // Bumped on regenerate to force-remount the PlateEditor so it picks up the
    // freshly-fetched template as its `initialValue` (the editor only reads
    // initialValue on mount).
    const [editorKey, setEditorKey] = useState(0);

    const editorContentRef = useRef<any[]>([]);
    const originalDocIdsRef = useRef<string[]>([]);
    const canvasRef = useRef<HTMLDivElement>(null);

    // Fallback: build a fresh template via the doc's own API flow when no saved
    // document exists. `isRegenerate=true` skips the editorReady reset so the
    // drawer chrome doesn't blink on regenerate.
    const fetchDataForDoc = useCallback(
        async (isRegenerate = false) => {
            if (!isRegenerate) setEditorReady(false);
            setLoading(true);
            try {
                const initialized = await buildInitialTemplate({
                    orgId,
                    investorOrgId: investorOrgId as string,
                    emailId: emailId as string,
                    companyName,
                    companyMainImg: companyMainImg as string,
                    dispatch,
                });
                setEditorInitialData(initialized);
                originalDocIdsRef.current = collectDocIdsFromSlate(initialized);
                editorContentRef.current = initialized;
                setEditorKey((k) => k + 1);
            } catch (err) {
                // Builder failed — fall through to ready so the drawer doesn't hang.
                console.error('[PlateDocumentDrawer] buildInitialTemplate failed', err);
            } finally {
                setEditorReady(true);
                setLoading(false);
            }
        },
        [orgId, investorOrgId, emailId, companyMainImg, companyName, dispatch, buildInitialTemplate, setLoading],
    );

    // Primary load: check for an already-saved document of this docType first;
    // fall back to fetchDataForDoc when none exists.
    const fetchTemplateData = useCallback(() => {
        setEditorReady(false);
        setLoading(true);
        getOnePagerTemplateData({
            assesseeOrgId: orgId,
            investorOrgId: investorOrgId as string,
            docType,
        })
            .then(({ templateData }) => {
                if (templateData?.length) {
                    setEditorInitialData(templateData);
                    originalDocIdsRef.current = collectDocIdsFromSlate(templateData);
                    setEditorReady(true);
                    setLoading(false);
                } else {
                    fetchDataForDoc();
                }
            })
            .catch(() => {
                fetchDataForDoc();
            });
    }, [orgId, investorOrgId, docType, fetchDataForDoc, setLoading]);

    useEffect(() => {
        dispatch(resetAndHideCommentsThread());
        fetchTemplateData();
        return () => {
            onClose();
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Re-fetch when orgId changes after mount (e.g. drawer reused for a different
    // org without unmounting). Skip the initial render — the mount effect above
    // already covers it, and running both in the same render causes two concurrent
    // fetches where the last response wins.
    const orgIdEffectFirstRun = useRef(true);
    useEffect(() => {
        if (orgIdEffectFirstRun.current) {
            orgIdEffectFirstRun.current = false;
            return;
        }
        if (orgId) fetchTemplateData();
    }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Convert Slate-shape legacy docs to Plate once at load; already-Plate docs
    // pass through. Apply the optional per-doc normalize transform.
    const initialValue = useMemo(() => {
        if (!Array.isArray(editorInitialData) || editorInitialData.length === 0) return undefined;
        const plate = isSlateShape(editorInitialData) ? slateToPlateValue(editorInitialData) : editorInitialData;
        return (normalizeOnLoad ? normalizeOnLoad(plate) : plate) as any;
    }, [editorInitialData, normalizeOnLoad]);

    const handlePlateChange = useCallback((plateValue: any) => {
        editorContentRef.current = plateValue;
    }, []);

    const safeExportName = useCallback(() => {
        const base = `${fileNamePrefix}${companyName ? `_${companyName}` : ''}`;
        return base.replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, '_');
    }, [fileNamePrefix, companyName]);

    const handleSaveAndClose = useCallback(async () => {
        if (saving) return;
        const content = editorContentRef.current?.length
            ? editorContentRef.current
            : Array.isArray(editorInitialData)
            ? editorInitialData
            : [];
        if (!content.length) {
            dispatch(closeCommentsThread());
            onClose();
            return;
        }
        setSaving(true);
        try {
            const processed = await processImagesBeforeSave(content, originalDocIdsRef.current, imageDocumentType);
            originalDocIdsRef.current = collectDocIdsFromSlate(processed);
            await saveOnePagerTemplateData({
                assesseeOrgId: orgId,
                investorOrgId: investorOrgId as string,
                onePagerData: processed,
                email: emailId as string,
                docType,
            });
            Mixpanel.track(mixpanelSaveEvent, {
                userRole: user?.accountType,
                pnoPlatform: getSubdomain(),
                userId: user?.userId,
                currentUrl: window.location.href,
                organizationId: orgId,
            });
            // View-to-attach (RECUR-23138): opened from Compose Email — capture
            // the rendered editor to a PDF and hand the docId back to the modal
            // (attachment chip) before the drawer unmounts.
            if (onComposeAttach && canvasRef.current) {
                await onComposeAttach(canvasRef.current, safeExportName());
            }
            dispatch(closeCommentsThread());
            onClose();
        } catch {
            toast.error('Save failed. Please try again.');
        } finally {
            setSaving(false);
        }
    }, [
        saving,
        editorInitialData,
        orgId,
        investorOrgId,
        emailId,
        user,
        dispatch,
        docType,
        imageDocumentType,
        mixpanelSaveEvent,
        onClose,
        onComposeAttach,
        safeExportName,
    ]);

    const isLoading = !!loading || !editorReady;
    const headerTitle = `${title}${companyName ? ` — ${companyName}` : ''}`;

    const [exportMenuOpen, setExportMenuOpen] = useState(false);
    const exportMenuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!exportMenuOpen) return;
        const onDocClick = (e: MouseEvent) => {
            if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) setExportMenuOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setExportMenuOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [exportMenuOpen]);

    const handleExportServer = async () => {
        if (!canvasRef.current || exporting) return;
        try {
            await exportProgress.track(() => exportEditorToPdfServer({ target: canvasRef.current!, filename: safeExportName() }));
        } catch {
            toast.error('PDF export failed. Please try again.');
        }
    };
    // DOCX — serializes the Plate value tree to a real Word document (reads the
    // JSON, not the DOM), so it needs no canvas target.
    const handleExportDocx = async () => {
        if (exporting) return;
        // PDF exports read the live DOM (canvasRef), which always reflects the
        // loaded doc. DOCX reads the JSON value, but editorContentRef only fills
        // once the user edits — so fall back to the displayed value (Plate-
        // converted) for an unedited doc, matching handleSaveAndClose.
        const current = editorContentRef.current?.length ? editorContentRef.current : initialValue ?? [];
        const value = isSlateShape(current) ? slateToPlateValue(current) : current;
        try {
            await exportProgress.track(() => exportEditorToDocxFile({ value, filename: safeExportName() }));
        } catch {
            toast.error('Word export failed. Please try again.');
        }
    };

    const headerActions = (
        <>
            <div className={styles.exportGroup} ref={exportMenuRef}>
                <button
                    type="button"
                    className={clsx(styles.exportBtn, exportProgress.isRunning && styles.exportBtnExporting)}
                    onClick={handleExportServer}
                    disabled={isLoading || exporting}
                    aria-label="Export as PDF"
                    title={`Export the ${title.toLowerCase()} as a paginated PDF`}
                >
                    {exportProgress.isRunning ? <ExportingLabel percent={exportProgress.percent} /> : <span>Export .pdf</span>}
                </button>
                <button
                    type="button"
                    className={styles.exportCaretBtn}
                    onClick={() => setExportMenuOpen((o) => !o)}
                    disabled={isLoading || exporting}
                    aria-label="Export options"
                    aria-haspopup="true"
                    aria-expanded={exportMenuOpen}
                    title="Other export formats"
                >
                    <img src={PURPLE_DROPDOWN_CARET} className={styles.exportCaret} alt="" aria-hidden />
                </button>
                {exportMenuOpen && (
                    <div className={styles.exportPopover} role="menu" aria-label="Export options">
                        <button
                            type="button"
                            role="menuitem"
                            className={styles.exportPopoverItem}
                            onClick={() => {
                                setExportMenuOpen(false);
                                handleExportServer();
                            }}
                            disabled={isLoading || exporting}
                        >
                            Export as .pdf
                        </button>
                        <button
                            type="button"
                            role="menuitem"
                            className={styles.exportPopoverItem}
                            onClick={() => {
                                setExportMenuOpen(false);
                                handleExportDocx();
                            }}
                            disabled={isLoading || exporting}
                            title="Export as an editable Word (.docx) document"
                        >
                            Export as .docx
                        </button>
                    </div>
                )}
                {exportProgress.isRunning && <ProgressBorder percent={exportProgress.percent} />}
            </div>
            <Button
                text="Save & Close"
                variant="primary"
                onClick={handleSaveAndClose}
                className={styles.headerSaveBtn}
                loading={saving}
                disabled={saving}
            />
        </>
    );

    return (
        <>
            <BottomDrawer
                open
                onClose={onClose}
                title={headerTitle}
                id={drawerId}
                headerRight={headerActions}
                height="100vh"
                className={styles.drawer}
                bodyClassName={styles.body}
            >
                <div className={styles.toolbarHost} id={toolbarHostId} />
                <div className={styles.editorRow}>
                    <div className={styles.canvas} ref={canvasRef}>
                        {isLoading ? (
                            <div className={styles.loadingPane}>
                                <LoadingSpinner />
                            </div>
                        ) : (
                            <PlateEditor
                                key={editorKey}
                                initialValue={initialValue}
                                className={styles.editor}
                                onChange={handlePlateChange}
                                fullHeight
                                toolbarHostId={toolbarHostId}
                                uploadSource={uploadSource}
                                mentionableUsers={ownerOptions}
                                enableAI={Boolean(aiEnabled.write)}
                                aiOrgId={orgId}
                            />
                        )}
                    </div>
                    <aside className={clsx(styles.commentsPane, commentsActive && styles.commentsPaneOpen)} aria-hidden={!commentsActive}>
                        <div id={commentContainerId} className={styles.commentsPortalTarget} />
                    </aside>
                </div>
                <div className={styles.footer}>
                    <div className={styles.footerRight}>
                        <button
                            type="button"
                            className={styles.regenerateBtn}
                            onClick={() => setShowRegenConfirmModal(true)}
                            disabled={isLoading}
                            aria-label={`Regenerate ${title.toLowerCase()}`}
                            title={`Regenerate ${title.toLowerCase()}`}
                        >
                            <img src={regenerateIcon} alt="" aria-hidden height="24" />
                            <span>Regenerate</span>
                        </button>
                        <PreviewButton
                            getTarget={() => canvasRef.current}
                            filename={safeExportName()}
                            disabled={isLoading}
                            className={styles.footerPreviewBtn}
                        />
                    </div>
                </div>
            </BottomDrawer>

            {showRegenConfirmModal && (
                <Modal
                    open={showRegenConfirmModal}
                    onClose={() => setShowRegenConfirmModal(false)}
                    size="flexible"
                    className={styles.regenModalOverDrawer}
                >
                    <div className={styles.confirmModal}>
                        <div className={styles.confirmTitle}>Regenerate {title}</div>
                        <div className={styles.confirmSubtext}>
                            Are you sure you want to regenerate the {title}? You will lose all the changes made so far.
                        </div>
                        <div className={styles.confirmButtons}>
                            <Button
                                text="Cancel"
                                variant="purpleinverted"
                                onClick={() => setShowRegenConfirmModal(false)}
                                style={{ marginTop: '0' }}
                            />
                            <Button
                                text="Proceed Anyway"
                                variant="primary"
                                onClick={() => {
                                    setShowRegenConfirmModal(false);
                                    fetchDataForDoc(true);
                                }}
                                style={{ marginTop: '0' }}
                            />
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
}

export default PlateDocumentDrawer;
